import { Component, useCallback, useEffect, useLayoutEffect, useState } from 'react';
import {
  getAdminPathname,
  getSiteHomePathname,
  isAdminPathname,
  notifyAppPathChanged,
} from './adminPath';
import { AdminGuestPortal } from './AdminGuestPortal';
import { isRemoteGuestRegistryConfigured } from './guestAllowlist';
import {
  assertCanAccessGuestRegistryAdmin,
  consumeFirebaseRedirectResult,
  firebaseSignInWithGoogle,
  firebaseSignOut,
  subscribeFirebaseAuth,
} from './firebaseRemoteSync';

const LEGACY_HASH = 'guest-registry-admin';

class AdminGuestPortalErrorBoundary extends Component {
  state = { crashed: false };

  static getDerivedStateFromError() {
    return { crashed: true };
  }

  componentDidCatch(err, info) {
    if (process.env.NODE_ENV === 'development') {
      console.error('AdminGuestPortal crashed', err, info);
    }
  }

  render() {
    if (this.state.crashed) {
      const { onClose } = this.props;
      return (
        <div
          role="alert"
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 10003,
            background: 'rgba(9,9,11,0.96)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 24,
            fontFamily: 'system-ui, sans-serif',
            color: '#fafafa',
          }}
        >
          <div style={{ maxWidth: 420, textAlign: 'center' }}>
            <h1 style={{ margin: '0 0 12px', fontSize: 18 }}>Guest admin couldn’t load</h1>
            <p style={{ margin: '0 0 20px', fontSize: 14, color: '#a1a1aa', lineHeight: 1.5 }}>
              A browser extension or strict privacy mode can cause this after sign-in. Try another browser or a
              private window with extensions off. If it keeps happening, open the browser console and share the red
              error line.
            </p>
            <button
              type="button"
              onClick={() => onClose?.()}
              style={{
                font: 'inherit',
                fontWeight: 600,
                padding: '10px 20px',
                borderRadius: 8,
                border: '1px solid #3f3f46',
                background: '#27272a',
                color: '#fafafa',
                cursor: 'pointer',
              }}
            >
              Back to site
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

/**
 * Production guest-list admin at /admin (respects PUBLIC_URL for GitHub project pages).
 * Old links #guest-registry-admin redirect here.
 */
export function ProductionGuestAdmin() {
  const [open, setOpen] = useState(() =>
    typeof window !== 'undefined' ? isAdminPathname(window.location.pathname) : false,
  );
  const [user, setUser] = useState(null);
  const [authBusy, setAuthBusy] = useState(false);
  const [authError, setAuthError] = useState('');
  /** Firebase user passed auth, but allowed-list check not done yet / failed */
  const [adminGate, setAdminGate] = useState('idle');

  useLayoutEffect(() => {
    if (typeof window === 'undefined') return;
    const h = window.location.hash.replace(/^#/, '').split('?')[0].trim();
    if (h === LEGACY_HASH) {
      window.location.replace(window.location.origin + getAdminPathname() + window.location.search);
    }
  }, []);

  useEffect(() => {
    if (!isRemoteGuestRegistryConfigured()) return () => {};
    const unsub = subscribeFirebaseAuth(setUser);
    return () => unsub();
  }, []);

  useEffect(() => {
    if (!isRemoteGuestRegistryConfigured()) return undefined;
    consumeFirebaseRedirectResult();
    return undefined;
  }, []);

  useEffect(() => {
    const sync = () => setOpen(isAdminPathname(window.location.pathname));
    sync();
    window.addEventListener('popstate', sync);
    return () => window.removeEventListener('popstate', sync);
  }, []);

  useEffect(() => {
    if (!isRemoteGuestRegistryConfigured()) {
      setAdminGate('idle');
      return undefined;
    }
    if (!user?.uid) {
      setAdminGate('idle');
      return undefined;
    }
    let cancelled = false;
    setAdminGate('checking');
    assertCanAccessGuestRegistryAdmin()
      .then(async (ok) => {
        if (cancelled) return;
        if (ok) {
          setAdminGate('ok');
          return;
        }
        setAuthError(
          'This account is not authorised to open the guest admin. Sign in with an address listed in Firestore config/registryAdmins (allowedEmails), or ask an organiser to add you.',
        );
        await firebaseSignOut();
        if (!cancelled) setAdminGate('idle');
      })
      .catch(() => {
        if (cancelled) return;
        setAuthError('Could not verify admin access. Check your connection and try again.');
        return firebaseSignOut().then(() => {
          if (!cancelled) setAdminGate('idle');
        });
      });
    return () => {
      cancelled = true;
    };
  }, [user?.uid]);

  const close = useCallback(() => {
    const home = getSiteHomePathname();
    window.history.replaceState(null, '', home);
    notifyAppPathChanged();
    setOpen(false);
    setAuthError('');
  }, []);

  const onGoogleSignIn = useCallback(async () => {
    setAuthError('');
    setAuthBusy(true);
    try {
      await firebaseSignInWithGoogle();
    } catch (err) {
      const code = err?.code;
      let msg = err?.message || 'Google sign-in failed.';
      if (code === 'auth/popup-blocked') {
        msg = 'Popup was blocked. Allow popups for this site and try again.';
      }
      if (code === 'auth/unauthorized-domain') {
        msg =
          'This domain is not allowed. In Firebase Console → Authentication → Settings → Authorized domains, add your site (e.g. almostsilver.town).';
      }
      setAuthError(msg);
    } finally {
      setAuthBusy(false);
    }
  }, []);

  if (!open) {
    return null;
  }

  if (!isRemoteGuestRegistryConfigured()) {
    return (
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Guest registry not configured"
        style={{
          position: 'fixed',
          inset: 0,
          zIndex: 10001,
          background: 'rgba(9,9,11,0.92)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 24,
        }}
      >
        <div
          style={{
            width: '100%',
            maxWidth: 440,
            background: '#18181b',
            border: '1px solid #3f3f46',
            borderRadius: 12,
            padding: '28px 24px',
            color: '#fafafa',
            fontFamily: 'system-ui, sans-serif',
          }}
        >
          <h1 style={{ margin: '0 0 10px', fontSize: 18, fontWeight: 700 }}>Guest registry admin</h1>
          <p style={{ margin: '0 0 14px', fontSize: 14, color: '#a1a1aa', lineHeight: 1.55 }}>
            This URL is correct, but the live registry is not enabled in this deployment. Create React App only
            includes Firebase if you set these at <strong style={{ color: '#fafafa' }}>build time</strong>, then
            rebuild and redeploy:
          </p>
          <ul
            style={{
              margin: '0 0 16px',
              paddingLeft: 20,
              fontSize: 13,
              color: '#d4d4d8',
              lineHeight: 1.6,
            }}
          >
            <li>
              <code style={{ fontSize: 12 }}>REACT_APP_FIREBASE_API_KEY</code>
            </li>
            <li>
              <code style={{ fontSize: 12 }}>REACT_APP_FIREBASE_AUTH_DOMAIN</code>
            </li>
            <li>
              <code style={{ fontSize: 12 }}>REACT_APP_FIREBASE_PROJECT_ID</code>
            </li>
            <li>
              <code style={{ fontSize: 12 }}>REACT_APP_FIREBASE_APP_ID</code>
            </li>
          </ul>
          <p style={{ margin: '0 0 18px', fontSize: 13, color: '#a1a1aa', lineHeight: 1.5 }}>
            See <code style={{ fontSize: 11 }}>firebase.env.example</code> and <code style={{ fontSize: 11 }}>FIREBASE_SETUP.txt</code>{' '}
            in the repo. For GitHub Pages, add secrets and pass them into the build step (or use a small workflow
            that runs <code style={{ fontSize: 11 }}>npm run build</code> with env vars).
          </p>
          <button
            type="button"
            onClick={close}
            style={{
              font: 'inherit',
              fontWeight: 600,
              padding: '10px 18px',
              borderRadius: 8,
              border: '1px solid #3f3f46',
              background: '#27272a',
              color: '#fafafa',
              cursor: 'pointer',
            }}
          >
            Close
          </button>
        </div>
      </div>
    );
  }

  if (user && adminGate === 'checking') {
    return (
      <div
        role="status"
        aria-live="polite"
        aria-busy="true"
        style={{
          position: 'fixed',
          inset: 0,
          zIndex: 10001,
          background: 'rgba(9,9,11,0.92)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 24,
          fontFamily: 'system-ui, sans-serif',
          color: '#fafafa',
        }}
      >
        <p style={{ margin: 0, fontSize: 15 }}>Checking admin access…</p>
      </div>
    );
  }

  if (user && adminGate === 'ok') {
    return (
      <AdminGuestPortalErrorBoundary onClose={close}>
        <AdminGuestPortal onClose={close} productionRemoteMode />
      </AdminGuestPortalErrorBoundary>
    );
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Admin sign in"
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 10001,
        background: 'rgba(9,9,11,0.92)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 24,
      }}
    >
      <div
        style={{
          width: '100%',
          maxWidth: 400,
          background: '#18181b',
          border: '1px solid #3f3f46',
          borderRadius: 12,
          padding: '28px 24px',
          color: '#fafafa',
          fontFamily: 'system-ui, sans-serif',
        }}
      >
        <h1 style={{ margin: '0 0 20px', fontSize: 18, fontWeight: 700, lineHeight: 1.35 }}>
          here to do some wadmin? 💕
        </h1>
        <button
          type="button"
          disabled={authBusy}
          onClick={onGoogleSignIn}
          style={{
            width: '100%',
            boxSizing: 'border-box',
            font: 'inherit',
            fontWeight: 600,
            padding: '12px 16px',
            borderRadius: 8,
            border: '1px solid #3f3f46',
            background: '#fafafa',
            color: '#1f1f1f',
            cursor: authBusy ? 'wait' : 'pointer',
            marginBottom: 16,
          }}
        >
          {authBusy ? 'Signing in…' : 'Continue with Google'}
        </button>
        {authError ? (
          <p style={{ margin: '0 0 16px', fontSize: 13, color: '#f87171', lineHeight: 1.45 }}>{authError}</p>
        ) : null}
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          <button
            type="button"
            onClick={close}
            style={{
              font: 'inherit',
              fontWeight: 600,
              padding: '10px 18px',
              borderRadius: 8,
              border: '1px solid #3f3f46',
              background: 'transparent',
              color: '#fafafa',
              cursor: 'pointer',
            }}
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}
